// R6 — the app catalog read. release-engine's GET /v1/apps.
//
// ⛔ SERVER-FILTERED, and that is why it is the AUTHORITY for "which apps" (D-11). The
// handler is
// `@FilterByResourcePermission('cp:app:read', { wildcardPermissions: ['cp:app:list-all'] })`
// on release-engine `AppsController.list` — a site-wide lister holding `cp:app:list-all`
// short-circuits to the full set; everyone else is narrowed to the apps they hold a
// `cp:app:read` grant on. The client does no filtering, and must not: `resourceGrants` is a
// client snapshot that can legitimately be null.
//
// ⛔ `api` comes from src/platform (D-15′ / WS-A §8), never from a fenced package.
import type { AppKind, AppSource, CreateAppRequest } from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

/**
 * ⛔ WIRE SHAPE, hand-declared because release-engine's app DTO has no entry in
 * @zion/sdk-contracts. Producer chain, verified:
 *   release-engine AppsController.list → AppsService.list → the `apps` row.
 * No contract test pins the row against a live response. The one field that has a contract,
 * `source`, imports its type from `@zion/sdk-contracts` (plan 2026-10-07-app-source-record, C-1).
 */
export interface App {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * The app source record: where the app's code lives (plan 2026-10-07-app-source-record, D7).
   * The `zion` CLI detects it on `zion app create`, `zion app update` and `zion app release`;
   * release-engine stores it on `release.apps` and maps the seven `source_*` columns to this
   * object.
   *
   * - `null`: nothing was ever detected for this app (every app registered before the feature).
   * - `{ vcs: 'none', detectedAt }`: the CLI ran in a directory outside Git.
   * - `{ vcs: 'git', repo, origin, ref, sha, path, detectedAt }`: a Git checkout. `repo` and
   *   `origin` are null when the checkout has no origin. `ref` is the full ref HEAD named,
   *   `refs/heads/<branch>` or `HEAD` when detached, and null when HEAD was unreadable. `sha` is
   *   the commit HEAD resolved to, null for a branch with no commit and for an unreadable HEAD
   *   (D23).
   *
   * Optional as well as nullable: a release-engine before migration 0032 sends no `source` key,
   * and the console and release-engine pods of one `zion-cp` release roll one at a time. The
   * console renders an absent key exactly as `null`. Only the Overview pane reads it
   * (`AppSourceLine`); no screen sends it back, because a browser cannot see a checkout (D20).
   */
  readonly source?: AppSource | null;
  /**
   * The kind of the app record (2026-10-12-shared-libraries, R-002, R-013): `app`, or `library` for a
   * shared library that apps use through `library.json`. release-engine's `toAppView` spreads the
   * row, so the 0038 column `release.apps.kind` reaches the wire (WS-18 decision 9).
   *
   * The list never carries the third kind, `instance`: `GET /v1/apps` leaves instance rows out
   * (R-013), and the console reads apps through the list alone. So the type is the narrower
   * `AppKind` (index default DEF-1).
   *
   * Optional: a release-engine before migration 0038 sends no `kind` key, and the console and
   * release-engine pods of one `zion-cp` release roll one at a time. The console renders an absent
   * key as `app` (`appKindLabel`). No screen sends it back: the first publish sets it.
   */
  readonly kind?: AppKind;
}

/**
 * ⛔ ONLY `listApps`. `GET /v1/apps/:id` is deliberately NOT used: the list already returns
 * the full row, and a second fetch on the overview pane would be a second, divergeable source
 * for the same data. An aborted `signal` stops the request and rejects the call with its reason.
 */
export function listApps(signal?: AbortSignal): Promise<readonly App[]> {
  return api.get<readonly App[]>(APPS_PATH, { signal });
}

/**
 * ⛔ The full literal, written whole so `fixture-routes-answer.test.ts` can grep it out of
 * `src/api/**`. ⚠️ Shared by the list (GET) and the create (POST) — the same path, two verbs, one
 * controller (`@Controller('v1/apps')`).
 */
export const APPS_PATH = '/v1/apps';

/**
 * ⛔⛔ **`cp:app:create`, NOT `cp:app:publish`** — R9 **SR-1**: read the gate off the PRODUCER.
 *
 * `release-engine/src/modules/apps/apps.controller.ts:112-113` is `@Post()` +
 * `@RequirePermission('cp:app:create')` — re-measured 2026-08-09; the previously-cited `:69-70` had
 * drifted.
 *
 * ⚠️ **The cautionary example is HISTORICAL — do not go looking for it.** The old console's
 * `NewAppPage.tsx:13` read `useHasPermission('cp:app:publish')`, and **one wrong line was wrong in
 * BOTH directions at once**: a holder of `cp:app:create` alone saw no button, and a holder of
 * `cp:app:publish` alone saw a button that 403s. That line was corrected to `cp:app:create` by
 * #511 (WS-F), and the whole console was deleted in WS-8 (2026-08-09). ⇒ The *defect* is gone; the
 * *rule it teaches* is why this constant exists, so it is kept rather than deleted.
 *
 * ⛔ **Do not "check both" with an `anyOf`** — that restores the second half of the defect, which is the
 * worse half: a door onto a refusal.
 */
export const APP_CREATE_PERMISSION = 'cp:app:create';

/**
 * The create body — ⛔ **`CreateAppRequest` from `@zion/sdk-contracts`, NOT a hand-declared mirror.**
 *
 * ## ⛔ WHY THIS ONE IS IMPORTED WHILE {@link App} ABOVE IS HAND-DECLARED
 *
 * {@link App}'s header states the rule and its reason: *"hand-declared because release-engine's app DTO
 * has no entry in `@zion/sdk-contracts`"*. That is true of the RESPONSE and false of the REQUEST —
 * `createAppSchema` **is** in contracts (`app-admin-inputs.ts:143`), and the release-engine DTO
 * (`dto/create-app.dto.ts`) declares its own copy under the **CR-8 rule**: *the pattern and the message
 * are imported, never re-declared, so this door and the contracts copy reject identically.* ⇒ importing
 * the type here puts the console on the same side of CR-8 as the producer, and CLAUDE.md says so
 * outright: *"Cross-package schemas live in `@zion/sdk-contracts`; consumers import `z.infer<>`."*
 *
 * ⚠️ A `type`-only import, so nothing is added to the bundle.
 *
 * ## The field set is the producer's; R9 `02a-PORTS.md` §4.12 named two of its fields wrong
 *
 * The plan's `CreateAppBody` reads `{ id, displayName, description?, ownerOperatorId? }`. Measured
 * against **both** copies of `createAppSchema`, which agree:
 *
 * | plan | producer | consequence of the plan's version |
 * |---|---|---|
 * | `id` | **`slug`** | `.strict()` ⇒ 400, unknown key |
 * | `displayName` | **`name`** | `.strict()` ⇒ 400, unknown key |
 * | `ownerOperatorId?` optional | `ownerOperatorId` declared required (`z.string().uuid()`) in the shared contract | the release-engine door accepts it absent (D7 of 2026-10-20-cp-permissioning) |
 *
 * Since D7 of 2026-10-20-cp-permissioning, `resolveAppOwner` in release-engine makes the caller the
 * owner when the body omits `ownerOperatorId`. A different owner needs `cp:platform:admin`, else 403.
 * The type below stays the shared contract's, which still declares the member required.
 */
export type CreateAppBody = CreateAppRequest;

/**
 * Create an app.
 *
 * ## ⛔⛔ THE PRODUCER WRITES THE OWNER GRANT **BEFORE** THE APP ROW — #1370
 *
 * `apps.controller.ts:71-72`'s own `@ApiOperation` says it: *"Create a new app (writes owner grant
 * FIRST, then app row)"*. ⇒ a failure between the two leaves **a resource grant naming an app that does
 * not exist**, and `apps.service.ts:303-316` records that the orphan is **permanent — nothing reaps it
 * and nothing can (#927)**, because the deferred owner trigger refuses every `DELETE` and every
 * `UPDATE revoked_at` on an app with no surviving owner grant.
 *
 * ⛔⛔ **SO THERE IS NO RETRY HERE, AND THERE MUST NEVER BE ONE.** A retry after a partial write can
 * produce **two** grants. The console surfaces the server's message verbatim and stops; the caller
 * decides. ⚠️ Also do not wrap this in a "create, then verify by listing" helper — a read that finds
 * nothing cannot distinguish *"the app row failed"* from *"the list is stale"*, and acting on that guess
 * is how the second grant gets written.
 *
 * ⚠️ **The likeliest failure is an ordinary duplicate slug**, which reaches the same path: `apps_slug_key`
 * (`db/schema/apps.ts:33`) is a plain unique constraint with no `ConflictException` and no filter on the
 * route, so it surfaces as a bare 500. Reported to the lead; the console's job is to not obscure it.
 */
export function createApp(body: CreateAppBody): Promise<App> {
  return api.post<App>(APPS_PATH, body);
}

// ── M-8 · edit app metadata (06b G5, producer S1) ────────────────────────────

/**
 * S1's gate.
 *
 * ⛔ `cp:app:update`, RESOURCE-scoped on the app (`apps.controller.ts:120-121`). ⚠️ **Not
 * `cp:app:write`**, which is a different grant on this same controller (it gates the build read) —
 * a principal can hold either without the other, so reusing one for both would light up a control
 * its holder cannot operate.
 */
export const APP_UPDATE_PERMISSION = 'cp:app:update';

/**
 * ⛔ The parameter is named `appId` and the literal interpolates **that name**, deliberately:
 * `fixture-routes-answer.test.ts:199-210` fills probe placeholders BY NAME, so a parameter called
 * `id` falls through to the catch-all and the gate probes `/v1/apps/<platform-version-uuid>` — the
 * wrong KIND of id, and a failure that looks exactly like a missing handler.
 */
export function appPath(appId: string): string {
  return `/v1/apps/${appId}`;
}

/** The producer's bound (`updateAppSchema`, `.min(1).max(120)`), so the field can refuse locally. */
export const APP_NAME_MAX = 120;

/**
 * The PATCH body.
 *
 * ⛔ HAND-DECLARED to match `updateAppSchema`
 * (`release-engine/src/modules/apps/dto/update-app.dto.ts`), which is `.strict()`:
 *  · **`slug` is IMMUTABLE and not in the schema** — sending it is a 400 on an unknown key.
 *  · **`description: null` CLEARS it; omitting the field leaves it UNCHANGED.** Those are two
 *    different requests, and the `string | null | undefined` union is what keeps them different.
 *    ⚠️ `''` is a third thing again: the producer accepts it (there is no `.min(1)`), but an app
 *    with `description: ''` and an app with none then render identically while being different
 *    rows — so this console sends `null` when the field is blanked, matching the ruling
 *    that `buildCreateAppBody` in NewAppPane.tsx already made for create.
 */
export interface UpdateAppBody {
  readonly name?: string;
  readonly description?: string | null;
}

export function updateApp(appId: string, body: UpdateAppBody): Promise<App> {
  return api.patch<App>(appPath(appId), body);
}
